import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  LOGIN_ROUTE,
  setCurrentStep,
  setOtpSent,
  checkPhoneNumberExists,
  sendOTP,
  signUp,
  validateOtp,
  getOtpButtonVisibility,
} from "@hypexone/uicore";
import StepOne from "./StepOne";
import Button from "../../components/shared/Button/Button";
import SocialAuth from "../social-auth/SocialAuth";
import { useSignUpModal } from "../../context/SignUpModalContext";


interface SignUpFormProps {
  onSuccess?: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSignUpModalOpen } = useSignUpModal();

  const { otpSent, phoneNumberExists } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);
  const { domainConfig } = useAppSelector((state) => state.common);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<number>();
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get("campaignId");
  const refCodeFromUrl = searchParams.get("refCode");
  const [campaignCode, setCampaignCode] = useState<string>(campaignId ?? "");
  const referralCode = refCodeFromUrl ?? "";
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const maxLength = 10;
    if (newValue.length <= maxLength && /^\d*$/.test(newValue)) {
      setPhoneNumber(newValue);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const showPasswordClickHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const rawCamp = campaignCode ?? "";
    const cleanCamp = rawCamp.trim().toLowerCase();
    const cleanRef = (referralCode ?? "").trim();
    setLoader(true);

    try {
      if (!otpSent && phoneNumber && agreedToTerms) {
        const { showSmsOtpButton, showWhatsappOtpButton } =
          getOtpButtonVisibility(domainConfig);
        const isWhatsapp = !showSmsOtpButton && showWhatsappOtpButton;
        await dispatch(
          sendOTP({ phoneNumber: "91" + phoneNumber, isWhatsapp })
        ).unwrap();
      } else if (otpSent && otp && (!username || !password)) {
        await dispatch(
          validateOtp({ phoneNumber: `91${phoneNumber}`, otp })
        ).unwrap();
      } else if (otpSent && username && password) {
        await dispatch(
          signUp({
            username: username.toLowerCase(),
            fullName: username.toLowerCase(),
            emailId: email,
            password,
            phoneNumber: "91" + phoneNumber,
            otp,
            referralCode: cleanRef,
            campaignCode: cleanCamp,
          })
        ).unwrap();
        onSuccess?.();
        navigate("/home");
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const redirectToSignIn = () => {
    setSignUpModalOpen(false);
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    if (campaignId) {
      localStorage.setItem("campaignId", campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    if (refCodeFromUrl) {
      localStorage.setItem("refCode", refCodeFromUrl);
    }
  }, [refCodeFromUrl]);

  useEffect(() => {
    dispatch(setCurrentStep(1));
    dispatch(setOtpSent(false));
  }, []);

  useEffect(() => {
    if (phoneNumber) {
      dispatch(checkPhoneNumberExists(phoneNumber));
    }
  }, [phoneNumber]);

  const isSubmitDisabled =
    !phoneNumber ||
    !agreedToTerms ||
    !isUsernameValid ||
    phoneNumberExists ||
    (otpSent && (!otp || !username || !password)) ||
    loader;

  return (
    <div className="w-full max-w-md">
      <header className="mb-3 flex flex-col items-center justify-center text-center">
        <h1 className="text-xl font-bold text-black3">
          {langData?.["sign_up"]}
        </h1>
        <p className="mt-1 text-xs text-black4">
          {langData?.["create_your_account_txt"]}
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-0.5 text-sm font-medium"
      >
        <StepOne
          otpSent={otpSent}
          otp={otp}
          setPhoneNumber={setPhoneNumber}
          phoneNumber={phoneNumber}
          agreedToTerms={agreedToTerms}
          email={email}
          onEmailChange={handleEmailChange}
          onPhoneChange={handlePhoneChange}
          onTermsChange={handleTermsChange}
          onTermsClick={() => navigate("/legal/terms")}
          username={username}
          password={password}
          setOtp={setOtp}
          showPassword={showPassword}
          onUsernameChange={handleUsernameChange}
          onPasswordChange={handlePasswordChange}
          showPasswordClickHandler={showPasswordClickHandler}
          onUsernameValidityChange={setIsUsernameValid}
          campaignCode={campaignCode}
          onCampaignCodeChange={setCampaignCode}
          referralCode={referralCode}
        />

        <Button
          type="submit"
          disabled={isSubmitDisabled}
          loading={loader}
          className={`mt-3 min-h-11 w-full rounded-xl px-4 py-2.5 text-center text-[14px] font-semibold shadow-[0_8px_20px_rgba(0,0,0,0.2)] transition-all duration-200 ${
            isSubmitDisabled
              ? "cursor-not-allowed bg-[#3a3a3a]/55 text-goldenYellow/50"
              : "bg-gradient-to-b from-[#2f2f2f] via-black to-[#141414] text-goldenYellow hover:brightness-105"
          }`}
        >
          {!otpSent
            ? langData?.["send_otp"]
            : otpSent && (!username || !password)
              ? langData?.["validate_otp"]
              : langData?.["sign_up"]}
        </Button>

        {/* <div className="mt-3 self-center text-center text-xs text-black4">
          {langData?.["already_have_an_account"]}{" "}
          <button
            type="button"
            onClick={redirectToSignIn}
            className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-black underline underline-offset-2 transition-opacity hover:opacity-80"
          >
            {langData?.["login"] ?? "Login"}
          </button>
        </div> */}

        {/* {domainConfig?.oauth_enabled && (
          <div className="mt-3 flex flex-col items-center gap-2 text-black4">
            <div className="flex w-full items-center gap-2">
              <span className="h-px flex-1 bg-black/15" />
              <span className="text-[11px] text-black4">or continue with</span>
              <span className="h-px flex-1 bg-black/15" />
            </div>
            <SocialAuth />
          </div>
        )} */}
      </form>
    </div>
  );
};

export default SignUpForm;
